/*! © 2026 AirX (airx.art). All rights reserved. Unauthorized copying, reproduction, or redistribution of this code is prohibited. Report infringement: info@airx.art */

(function () {
  'use strict';

  const AirX = (window.AirX = window.AirX || {});
  const bus = new EventTarget();

  window.Eurus = window.Eurus || {};
  window.Eurus.loadedScript = window.Eurus.loadedScript || new Set();
  window.Eurus.cart_add_url = window.Eurus.cart_add_url || (window.AirXRoutes && window.AirXRoutes.cart_add_url) || '/cart/add';
  window.Eurus.cart_error = window.Eurus.cart_error || (window.AirXStrings && window.AirXStrings.cartError) || 'Something went wrong.';

  AirX.on = (name, fn) => bus.addEventListener(name, fn);
  AirX.off = (name, fn) => bus.removeEventListener(name, fn);
  AirX.emit = (name, detail) => bus.dispatchEvent(new CustomEvent(name, { detail }));

  AirX.debounce = (fn, wait = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  AirX.fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  });

  AirX.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || (window.AirXSettings && window.AirXSettings.moneyFormat) || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }
    let value = '';
    switch (format.match(placeholderRegex)[1]) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  };

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trapped = null;

  AirX.trapFocus = (container, initial) => {
    AirX.releaseFocus();
    const focusables = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === initial
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last && last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first && first.focus(); }
    };
    container.addEventListener('keydown', handler);
    trapped = { container, handler, previous: document.activeElement };
    (initial || first || container).focus({ preventScroll: true });
  };

  AirX.releaseFocus = (container) => {
    if (!trapped) return;
    if (container && trapped.container !== container) return;
    trapped.container.removeEventListener('keydown', trapped.handler);
    if (trapped.previous && trapped.previous.focus) trapped.previous.focus({ preventScroll: true });
    trapped = null;
  };

  let backdrop = null;
  const openOverlays = new Set();

  function ensureBackdrop() {
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'overlay-backdrop';
      backdrop.addEventListener('click', () => AirX.emit('overlay:backdrop-click'));
      document.body.appendChild(backdrop);
    }
    return backdrop;
  }

  const overlayHosts = new Map();
  function liftOverlayHost(id) {
    const el = typeof id === 'string' ? document.getElementById(id) : null;
    const host = el && el.closest('.shopify-section');
    if (!host || host === el) return;
    overlayHosts.set(id, host);
    host.classList.add('is-overlay-host');
  }
  function dropOverlayHost(id) {
    const host = overlayHosts.get(id);
    if (!host) return;
    overlayHosts.delete(id);
    for (const other of overlayHosts.values()) if (other === host) return;
    host.classList.remove('is-overlay-host');
  }

  AirX.overlayOpened = (id) => {
    openOverlays.add(id);
    liftOverlayHost(id);
    ensureBackdrop().classList.add('is-open');
    document.body.classList.add('overlay-open');
  };
  AirX.overlayClosed = (id) => {
    openOverlays.delete(id);
    dropOverlayHost(id);
    if (openOverlays.size === 0) {
      if (backdrop) backdrop.classList.remove('is-open');
      document.body.classList.remove('overlay-open');
    }
  };

  const routes = window.AirXRoutes || {};

  async function parseCartResponse(response) {
    const data = await response.json();
    if (data.status && data.status !== 200) {
      const err = new Error(data.description || data.message || (window.AirXStrings && window.AirXStrings.cartError) || 'Cart error');
      err.status = Number(data.status) || response.status;
      throw err;
    }
    return data;
  }

  AirX.cart = {
    sectionsToRender: ['cart-drawer', 'cart-icon-bubble'],

    async get() {
      const res = await fetch(`${routes.cart_url}.js`);
      return res.json();
    },

    async add(items, opts = {}) {
      const body = {
        items: Array.isArray(items) ? items : [items],
        sections: opts.sections || AirX.cart.sectionsToRender,
        sections_url: window.location.pathname,
      };
      const res = await fetch(routes.cart_add_url + '.js', {
        ...AirX.fetchConfig(),
        body: JSON.stringify(body),
      });
      const data = await parseCartResponse(res);

      AirX.emit('cart:updated', { source: opts.silent ? 'add-silent' : 'add', data, sections: data.sections });
      return data;
    },

    async change(payload, opts = {}) {
      const body = {
        ...payload,
        sections: opts.sections || AirX.cart.sectionsToRender,
        sections_url: window.location.pathname,
      };
      const res = await fetch(routes.cart_change_url + '.js', {
        ...AirX.fetchConfig(),
        body: JSON.stringify(body),
      });
      const data = await parseCartResponse(res);
      AirX.emit('cart:updated', { source: 'change', data, sections: data.sections });
      return data;
    },

    async update(payload, opts = {}) {
      const body = {
        ...payload,
        sections: opts.sections || AirX.cart.sectionsToRender,
        sections_url: window.location.pathname,
      };
      const res = await fetch(routes.cart_update_url + '.js', {
        ...AirX.fetchConfig(),
        body: JSON.stringify(body),
      });
      const data = await parseCartResponse(res);
      AirX.emit('cart:updated', { source: 'update', data, sections: data.sections });
      return data;
    },
  };

  const ADDON_FOR = '_Addon For';
  const ADDON_REF = '_Reference ID';
  const ADDON_KIND = '_Addon';

  const ADDON_HANDLES = ['canvas-coating'];

  const ADDON_PROPS = { Coating: 'coating' };
  let addonSync = null;
  let addonSyncTried = '';
  let addonSyncLog = [];

  const addonProps = (item) => (item && item.properties) || {};
  const isAddonLine = (item) => !!addonProps(item)[ADDON_FOR] || ADDON_HANDLES.indexOf(item && item.handle) > -1;

  function addonTargets(qtys, want) {
    const out = qtys.slice();
    let excess = qtys.reduce((sum, q) => sum + q, 0) - want;
    for (let i = out.length - 1; i >= 0 && excess > 0; i--) {
      const cut = Math.min(out[i], excess);
      out[i] -= cut;
      excess -= cut;
    }
    if (excess < 0 && out.length) out[0] -= excess;
    return out;
  }

  AirX.cart.addonFixes = (cart) => {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const canvasQty = new Map();
    items.forEach((item) => {
      const props = addonProps(item);
      if (isAddonLine(item) || !props[ADDON_REF]) return;
      canvasQty.set(props[ADDON_REF], (canvasQty.get(props[ADDON_REF]) || 0) + (item.quantity || 0));
    });
    const groups = new Map();
    items.forEach((item) => {
      if (!isAddonLine(item)) return;
      const props = addonProps(item);
      const ref = props[ADDON_FOR] || '';
      const id = ref + '\n' + (props[ADDON_KIND] || '');
      if (!groups.has(id)) groups.set(id, { want: ref ? canvasQty.get(ref) || 0 : 0, lines: [] });
      groups.get(id).lines.push(item);
    });
    let updates = null;
    groups.forEach((group) => {
      const qtys = group.lines.map((item) => item.quantity || 0);
      addonTargets(qtys, group.want).forEach((q, i) => {
        if (q !== qtys[i]) (updates = updates || {})[group.lines[i].key] = q;
      });
    });
    return updates;
  };

  AirX.cart.addonStrips = (cart) => {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const present = new Set();
    items.forEach((item) => {
      const props = addonProps(item);
      if (isAddonLine(item) && props[ADDON_FOR] && item.quantity > 0) present.add(props[ADDON_FOR] + '\n' + (props[ADDON_KIND] || ''));
    });
    const strips = [];
    items.forEach((item) => {
      const props = addonProps(item);
      if (isAddonLine(item) || !props[ADDON_REF]) return;
      const drop = Object.keys(ADDON_PROPS).filter((name) => props[name] && !present.has(props[ADDON_REF] + '\n' + ADDON_PROPS[name]));
      if (!drop.length) return;
      if (Object.keys(props).some((k) => String(props[k]).indexOf('pending://') === 0)) return;
      const properties = {};
      Object.keys(props).forEach((k) => { if (drop.indexOf(k) === -1) properties[k] = props[k]; });
      strips.push({ id: item.key, quantity: item.quantity, properties });
    });
    return strips;
  };

  AirX.cart.addonUpdates = (row, key, quantity) => {
    const ref = row && row.getAttribute('data-ref-id');
    const scope = row && (row.parentElement || row);
    if (!ref || !scope) return null;
    const addons = Array.from(scope.querySelectorAll('[data-cart-addon][data-line-key]'))
      .filter((el) => el.getAttribute('data-addon-for') === ref);
    if (!addons.length) return null;
    let want = quantity;
    scope.querySelectorAll('[data-cart-item][data-ref-id]').forEach((other) => {
      if (other === row || other.getAttribute('data-ref-id') !== ref) return;
      const input = other.querySelector('[data-cart-qty]');
      want += parseInt(input && input.getAttribute('value'), 10) || 0;
    });
    const updates = { [key]: quantity };
    const kinds = new Map();
    addons.forEach((el) => {
      const kind = el.getAttribute('data-addon-type') || '';
      if (!kinds.has(kind)) kinds.set(kind, []);
      kinds.get(kind).push(el);
    });
    kinds.forEach((els) => {
      const qtys = els.map((el) => parseInt(el.getAttribute('data-quantity'), 10) || 0);
      addonTargets(qtys, want).forEach((q, i) => {
        if (q !== qtys[i]) updates[els[i].getAttribute('data-line-key')] = q;
      });
    });
    return updates;
  };

  AirX.cart.addonState = (root) => {
    if (!root) return 'none';
    if (root.querySelector('[data-addon-missing]')) return 'fix';
    const addons = root.querySelectorAll('[data-addon-for]');
    if (!addons.length) return 'none';
    const totals = new Map();
    for (const el of addons) {
      if (el.hasAttribute('data-cart-item')) return 'fix';
      const ref = el.getAttribute('data-addon-for');
      const id = ref + '\n' + (el.getAttribute('data-addon-type') || '');
      if (!totals.has(id)) totals.set(id, { ref, qty: 0 });
      totals.get(id).qty += parseInt(el.getAttribute('data-quantity'), 10) || 0;
    }
    const rows = root.querySelectorAll('[data-cart-item][data-ref-id]');
    for (const t of totals.values()) {
      let canvas = 0;
      rows.forEach((row) => {
        if (row.getAttribute('data-ref-id') !== t.ref) return;
        const input = row.querySelector('[data-cart-qty]');
        canvas += parseInt(input && input.getAttribute('value'), 10) || 0;
      });
      if (canvas !== t.qty) return 'fix';
    }
    return 'ok';
  };

  AirX.cart.syncAddons = (cart, opts = {}) => {
    if (addonSync) return addonSync;
    const nextFix = (data) => AirX.cart.addonFixes(data) || AirX.cart.addonStrips(data)[0] || null;
    const send = async (fix) => {
      const signature = JSON.stringify(fix);
      const now = Date.now();
      addonSyncLog = addonSyncLog.filter((t) => now - t < 30000);
      if (signature === addonSyncTried || addonSyncLog.length >= 3) return null;
      addonSyncTried = signature;
      addonSyncLog.push(now);
      try {
        return fix.properties ? await AirX.cart.change(fix, opts) : await AirX.cart.update({ updates: fix }, opts);
      } catch (err) {

        if (!(err && err.status >= 400 && err.status < 500 && err.status !== 429)) addonSyncTried = '';
        throw err;
      }
    };
    addonSync = (async () => {
      let data = cart && cart.token && Array.isArray(cart.items) ? cart : await AirX.cart.get();
      let fix = nextFix(data);
      if (!fix) {
        addonSyncTried = '';
        return null;
      }

      for (let rounds = (data.items || []).length; fix && rounds > 0; rounds--) {
        const fixed = await send(fix);
        if (!fixed) return null;
        data = fixed;
        fix = nextFix(data);
      }
      if (!fix) addonSyncTried = '';
      return data;
    })().finally(() => {
      addonSync = null;
    });
    return addonSync;
  };

  AirX.fetchSection = async (sectionId, url = window.location.pathname, params = '') => {
    const join = params ? (params.startsWith('?') ? params + '&' : '?' + params + '&') : '?';
    const res = await fetch(`${url}${join}section_id=${sectionId}`);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc;
  };

  class AirXDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.drawer');
      this.id = this.id || `drawer-${Math.random().toString(36).slice(2, 8)}`;
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      AirX.on('overlay:backdrop-click', () => { if (this.isOpen) this.close(); });
      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
    }
    get isOpen() { return this.panel && this.panel.classList.contains('is-open'); }
    open(trigger) {
      if (!this.panel || this.isOpen) return;
      this.trigger = trigger;
      this.panel.classList.add('is-open');
      this.panel.setAttribute('aria-hidden', 'false');
      AirX.overlayOpened(this.id);
      AirX.trapFocus(this.panel, this.panel.querySelector('[data-drawer-close]'));
      AirX.emit('drawer:open', { id: this.id, drawer: this });
    }
    close() {
      if (!this.panel || !this.isOpen) return;
      this.panel.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      AirX.overlayClosed(this.id);

      AirX.releaseFocus(this.panel);
      AirX.emit('drawer:close', { id: this.id, drawer: this });
    }
    toggle(trigger) { this.isOpen ? this.close() : this.open(trigger); }
  }
  AirX.DrawerElement = AirXDrawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-target]');
    if (!opener) return;
    const target = document.querySelector(opener.getAttribute('data-drawer-target'));
    if (target && typeof target.open === 'function') {
      e.preventDefault();
      target.toggle ? target.toggle(opener) : target.open(opener);
    }
  });

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          if (button.name === 'plus') this.input.stepUp();
          else this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        })
      );
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  class ColorModeToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button') || this;
      this.button.addEventListener('click', () => {
        const html = document.documentElement;
        const next = html.getAttribute('data-color-mode') === 'dark' ? 'light' : 'dark';
        html.setAttribute('data-color-mode', next);
        try { localStorage.setItem('airx:color-mode', next); } catch (e) {}
        AirX.emit('color-mode:change', { mode: next });
      });
    }
  }
  if (!customElements.get('color-mode-toggle')) customElements.define('color-mode-toggle', ColorModeToggle);

  class AirXDetails extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.summary ? this.summary.nextElementSibling : null;
      if (!this.details || !this.summary || !this.content) return;
      this.summary.setAttribute('aria-expanded', String(this.details.open));
      this.summary.addEventListener('click', (e) => {
        e.preventDefault();
        this.details.open ? this.collapse() : this.expand();
      });
    }
    expand() {
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      if (window.gsap && document.documentElement.classList.contains('motion')) {
        window.gsap.fromTo(this.content, { height: 0, opacity: 0 }, {
          height: 'auto', opacity: 1, duration: 0.45, ease: 'power3.out',
          onComplete: () => window.gsap.set(this.content, { clearProps: 'height' }),
        });
      }
    }
    collapse() {
      this.summary.setAttribute('aria-expanded', 'false');
      if (window.gsap && document.documentElement.classList.contains('motion')) {
        window.gsap.to(this.content, {
          height: 0, opacity: 0, duration: 0.35, ease: 'power2.in',
          onComplete: () => { this.details.open = false; window.gsap.set(this.content, { clearProps: 'height,opacity' }); },
        });
      } else {
        this.details.open = false;
      }
    }
  }
  if (!customElements.get('airx-details')) customElements.define('airx-details', AirXDetails);

  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const poster = this.querySelector('[data-deferred-poster]');
      if (poster) poster.addEventListener('click', () => this.loadMedia());
    }
    loadMedia() {
      const template = this.querySelector('template');
      if (!template) return;
      const content = template.content.cloneNode(true);
      this.innerHTML = '';
      this.appendChild(content);
      const video = this.querySelector('video');
      if (video) video.play().catch(() => {});
    }
  }
  if (!customElements.get('deferred-media')) customElements.define('deferred-media', DeferredMedia);

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.setError('');
      this.submitButton.classList.add('btn--loading');
      this.submitButton.setAttribute('aria-disabled', 'true');
      try {
        const formData = new FormData(this.form);
        const items = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1), properties: {} };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) items.selling_plan = Number(sellingPlan);
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value) items.properties[match[1]] = value;
        }
        await AirX.cart.add(items);
        const media = document.querySelector('[data-main-product-image]') || this.closest('[data-product-card]')?.querySelector('img');
        if (media && AirX.flyToCart) AirX.flyToCart(media);
        AirX.emit('product:added', { form: this.form });
      } catch (err) {
        this.setError(err.message);
      } finally {
        this.submitButton.classList.remove('btn--loading');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }
    setError(message) {
      const el = this.querySelector('[data-form-error]');
      if (el) { el.textContent = message; el.hidden = !message; }
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  AirX.on('cart:updated', (e) => {
    const sections = e.detail && e.detail.sections;
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      document.querySelectorAll('[data-cart-icon-bubble]').forEach((el) => {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const fresh = doc.querySelector('[data-cart-icon-bubble]');
        if (fresh) el.innerHTML = fresh.innerHTML;
        const dot = el.querySelector('[data-cart-dot]');
        if (dot) { dot.classList.remove('pulse-once'); void dot.offsetWidth; dot.classList.add('pulse-once'); }
      });
    }
  });
})();
